import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import Avatar from '@components/avatar';
import { BASE_IMG_URL } from '@images/imageUrls';
import EmojiPicker from '@components/EmojiPicker/pc';
import { Image, Spin } from 'antd';
import Toast from '@components/Toast';
import { uploadAttachment, postsCreate } from '../../../server/api';

interface CommentInputProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    type?: 'post' | 'reply';  // 'post' 主评论 'reply' 回复评论
    threadId?: any;
    postId?: any;  // 如果是回复，需要提供被回复的评论 id（主楼ID）
    commentPostId?: any; // 回复二级评论时，目标评论ID
    replyToUser?: any;  // 回复对象用户信息 { nickname, userId }
    commentType?: number;  // 发布评论时的type参数 0=帖子评论 2=活动评论，默认0
    onSubmit?: (data: { content: string; images: any[]; newComment?: any }) => Promise<any>;
    onCancel?: () => void;
}

interface CommentInputState {
    content: string;
    images: any[];
    emojiPickerVisible: boolean;
    uploading: boolean;
}

@inject('app', 'site', 'user')
@observer
class CommentInput extends React.Component<CommentInputProps, CommentInputState> {
    private fileInputRef = React.createRef<HTMLInputElement>();
    private textareaRef = React.createRef<HTMLTextAreaElement>();

    state: CommentInputState = {
        content: '',
        images: [],
        emojiPickerVisible: false,
        uploading: false,
    };

    componentDidMount() {
        // 表情从 EmojiPopup 独立管理
    }

    // 处理文本内容变化
    handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ content: e.target.value });
    };

    // 处理表情选择
    handleEmojiSelect = (emoji: any) => {
        const { content } = this.state;
        const textarea = this.textareaRef.current;
        if (textarea) {
            const { selectionStart = 0 } = textarea;
            const newContent = content.slice(0, selectionStart) + emoji.code + content.slice(selectionStart);
            this.setState({ content: newContent }, () => {
                setTimeout(() => {
                    if (textarea) {
                        textarea.focus();
                        textarea.setSelectionRange(selectionStart + emoji.code.length, selectionStart + emoji.code.length);
                    }
                }, 0);
            });
        }
    };

    // 打开表情选择器
    handleEmojiClick = () => {
        this.setState({ emojiPickerVisible: true });
    };

    // 关闭表情选择器
    handleEmojiClose = () => {
        this.setState({ emojiPickerVisible: false });
    };

    // 处理上传图片
    handlePhotoClick = () => {
        const { images } = this.state;
        if (images.length >= 1) {
            Toast.info('最多只能上传1张图片');
            return;
        }
        this.fileInputRef.current?.click();
    };

    // 处理文件选择
    handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        const { images } = this.state;
        if (images.length + files.length > 1) {
            Toast.info('最多只能上传1张图片');
            return;
        }

        this.setState({ uploading: true });
        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const formData = new FormData();
                formData.append('type', '1');  // 1-图片
                formData.append('file', file);
                
                // 调用现有的上传附件接口
                const res = await uploadAttachment(formData);
                if (res && res.code === 0 && res.data) {
                    this.setState(prev => ({
                        images: [...prev.images, {
                            url: res.data!.url,
                            id: res.data!.id || Math.random().toString(36).substr(2, 9)
                        }]
                    }));
                } else {
                    Toast.info('图片上传失败');
                }
            }
        } catch (error) {
            console.error('上传错误:', error);
            Toast.info('图片上传出错');
        } finally {
            this.setState({ uploading: false });
            if (this.fileInputRef.current) {
                this.fileInputRef.current.value = '';
            }
        }
    };

    // 删除图片
    handleRemoveImage = (id: string) => {
        this.setState(prev => ({
            images: prev.images.filter(img => img.id !== id)
        }));
    };

    // 提交评论
    handleSubmit = async () => {
        const { content, images } = this.state;
        const { onSubmit, threadId, postId, commentPostId, type, commentType = 0 } = this.props;

        if (!content.trim()) {
            Toast.info('请输入评论内容');
            return;
        }

        this.setState({ uploading: true });

        // 构造接口参数
        const params: any = {
            id: threadId,          // 线程ID
            type: commentType,     // 评论类型：0=帖子评论 2=活动评论
            content: content,
            source: '',
        };

        if (type === 'reply' && postId) {
            params.replyPostId = postId;                // 楼中楼：被回复的主楼ID
            if (commentPostId) params.commentPostId = commentPostId; // 楼中楼回复他人：目标评论ID
        }

        // 图片附件，携带顺序
        if (images.length > 0) {
            params.attachments = images.map((img, index) => ({ id: img.id, order: index + 1 }));
        }

        // 调用接口
        const res = await postsCreate(params);

        if (res && (res.Code === 0 || res.code === 0) && (res.Data || res.data)) {
            const data = res.Data || res.data;
            Toast.success('发布成功');

            // 清空输入框
            this.setState({ content: '', images: [], emojiPickerVisible: false });

            // 如果有自定义的 onSubmit 回调，传递格式化后的新评论数据
            if (onSubmit) {
                const { replyToUser } = this.props;
                const newComment = {
                    id: data.id,
                    type: data.type,
                    content: data.content,
                    plainText: data.plainText,
                    source: data.source || '',
                    ipRegion: data.ipRegion || '',
                    commentDecoration: data.commentDecoration,
                    createdAt: data.createdAt,
                    updatedAt: data.updatedAt,
                    likeCount: data.likeCount || 0,
                    replyCount: data.replyCount || 0,
                    isLiked: data.isLiked || false,
                    isSink: data.isSink || false,
                    isSticky: data.isSticky || false,
                    user: data.user,
                    images: data.images || [],
                    postable: data.postable,
                    permissions: data.permissions,
                    replyPostId: data.replyPostId,
                    replyUserId: data.replyUserId,
                    commentPostId: data.commentPostId,
                    commentUserId: data.commentUserId,
                    replyUser: data.replyUser,
                    commentUser: data.commentUser,
                };

                // 如果 commentPostId 有值，说明是回复二级评论，赋值给 commentUser
                // 否则赋值给 replyUser
                if (commentPostId && replyToUser) {
                    if (!newComment.commentUser) {
                        newComment.commentUser = replyToUser;
                    }
                    if (!newComment.commentUserId && replyToUser.id) {
                        newComment.commentUserId = replyToUser.id;
                    }
                } else if (!newComment.replyUser && replyToUser) {
                    newComment.replyUser = replyToUser;
                    if (!newComment.replyUserId && replyToUser.id) {
                        newComment.replyUserId = replyToUser.id;
                    }
                }

                await onSubmit({ content, images, newComment });
            }
        } else {
            const msg = res?.Message || res?.msg || '发布失败';
            Toast.info(msg);
        }

        this.setState({ uploading: false });
    };

    // 取消
    handleCancel = () => {
        this.setState({ content: '', images: [], emojiPickerVisible: false });
        this.props.onCancel?.();
    };

    render() {
        const { content, images, emojiPickerVisible, uploading } = this.state;
        const { type = 'post', user, replyToUser } = this.props;
        const isPost = type === 'post';
        const containerClass = isPost ? styles.commentInputContainer : styles.replyInputContainer;

        // 回复样式保持不变
        if (!isPost) {
            return (
                <div className={containerClass}>
                    {/* {replyToUser && (
                        <div className={styles.replyToInfo}>
                            回复 <span className={styles.replyToName}>{replyToUser.nickname}</span>
                        </div>
                    )} */}

                    <Spin spinning={uploading}>
                        <div className={styles.inputWrapper}>
                            {/* <Avatar avatarUrl={user?.avatar} size={28} titleMark={user?.titleMark} userCode={user?.code} /> */}

                            <div className={styles.inputContent}>
                                {/* 输入框 */}
                                <textarea
                                    ref={this.textareaRef}
                                    className={styles.textarea}
                                    placeholder='回复评论...'
                                    value={content}
                                    onChange={this.handleContentChange}
                                />

                                {/* 图片预览 */}
                                {images.length > 0 && (
                                    <div className={styles.imagePreview}>
                                        {images.map((img) => (
                                            <div key={img.id} className={styles.imageItem}>
                                                <Image
                                                    width={80}
                                                    height={80}
                                                    src={img.url}
                                                    preview={{ mask: '预览' }}
                                                />
                                                <div
                                                    className={styles.removeIcon}
                                                    onClick={() => this.handleRemoveImage(img.id)}
                                                >
                                                    ×
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* 工具栏 */}
                                <div className={styles.toolbar}>
                                    <div className={styles.toolIcons}>
                                        <img
                                            src={`${BASE_IMG_URL}/common/setPost/photoBar.png`}
                                            alt="upload"
                                            className={styles.toolIcon}
                                            onClick={this.handlePhotoClick}
                                            title="上传图片"
                                        />
                                        <img
                                            src={`${BASE_IMG_URL}/common/setPost/expression.png`}
                                            alt="emoji"
                                            className={styles.toolIcon}
                                            onClick={this.handleEmojiClick}
                                            title="选择表情"
                                        />
                                    </div>

                                    <div className={styles.buttonGroup}>
                                        <button
                                            className={styles.cancelBtn}
                                            onClick={this.handleCancel}
                                        >
                                            取消
                                        </button>
                                        <button
                                            className={`${styles.submitBtn} ${content.trim() ? styles.active : ''}`}
                                            onClick={this.handleSubmit}
                                            disabled={!content.trim()}
                                        >
                                            发送
                                        </button>
                                    </div>
                                </div>

                                {/* 表情选择器 */}
                                {emojiPickerVisible && (
                                    <EmojiPicker
                                        visible={emojiPickerVisible}
                                        onClose={this.handleEmojiClose}
                                        onSelect={(emojiValue: string, emojiCode?: string) => this.handleEmojiSelect({ code: emojiCode || emojiValue })}
                                        showEmojiTab
                                        closeOnSelect={false}
                                        position={{ bottom: -320, left: 0 }}
                                    />
                                )}
                            </div>
                        </div>
                    </Spin>

                    {/* 隐藏的文件输入 */}
                    <input
                        ref={this.fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={this.handleFileChange}
                        style={{ display: 'none' }}
                    />
                </div>
            );
        }

        // 主评论样式：头像在灰色框外
        return (
            <div className={containerClass}>
                {/* 左侧头像 */}
                <Avatar avatarUrl={user?.avatar} titleMark={user?.titleMark} size={50} userCode={user?.code} />
                
                {/* 灰色背景框 */}
                <div className={styles.commentInputBox}>
                    <Spin spinning={uploading}>
                        <div className={styles.inputContent}>
                            {/* 输入框 */}
                            <textarea
                                ref={this.textareaRef}
                                className={styles.textarea}
                                placeholder='分享脑洞'
                                value={content}
                                onChange={this.handleContentChange}
                            />

                            {/* 图片预览 */}
                            {images.length > 0 && (
                                <div className={styles.imagePreview}>
                                    {images.map((img) => (
                                        <div key={img.id} className={styles.imageItem}>
                                            <Image
                                                width={110}
                                                height={110}
                                                src={img.url}
                                                preview={{ mask: '预览' }}
                                            />
                                            <div
                                                className={styles.removeIcon}
                                                onClick={() => this.handleRemoveImage(img.id)}
                                            >
                                                ×
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* 工具栏 */}
                            <div className={styles.toolbar}>
                                <div className={styles.toolIcons}>
                                    <img
                                        src={`${BASE_IMG_URL}/common/setPost/photoBar.png`}
                                        alt="upload"
                                        className={styles.toolIcon}
                                        onClick={this.handlePhotoClick}
                                        title="上传图片"
                                    />
                                    <img
                                        src={`${BASE_IMG_URL}/common/setPost/expression.png`}
                                        alt="emoji"
                                        className={styles.toolIcon}
                                        onClick={this.handleEmojiClick}
                                        title="选择表情"
                                    />
                                </div>

                                <div className={styles.buttonGroup}>
                                    <button
                                        className={`${styles.submitBtn} ${content.trim() ? styles.active : ''}`}
                                        onClick={this.handleSubmit}
                                        disabled={!content.trim()}
                                    >
                                        发送
                                    </button>
                                </div>
                            </div>

                            {/* 表情选择器 */}
                            {emojiPickerVisible && (
                                <EmojiPicker
                                    visible={emojiPickerVisible}
                                    onClose={this.handleEmojiClose}
                                    onSelect={(emojiValue: string, emojiCode?: string) => this.handleEmojiSelect({ code: emojiCode || emojiValue })}
                                    showEmojiTab
                                    closeOnSelect={false}
                                    position={{ bottom: -320, left: 0 }}
                                />
                            )}
                        </div>
                    </Spin>
                </div>

                {/* 隐藏的文件输入 */}
                <input
                    ref={this.fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={this.handleFileChange}
                    style={{ display: 'none' }}
                />
            </div>
        );
    }
}

export default CommentInput;
